/**
 * Where the person is standing, in about forty words.
 *
 * "Why isn't this working?" is not a question anybody can answer without knowing
 * what they are looking at. So every question carries a snapshot of the moment
 * it was asked: the screen, whatever is open over it, who is asking and what
 * they are allowed to do, the mode, the language, and how big the family is.
 *
 * What it deliberately does *not* carry is the family. Twenty-seven people with
 * names, dates, places and stories is a large, slow, mostly irrelevant thing to
 * send with "how do I make the text bigger". When a question really is about the
 * family — "when is Miriam's birthday?" — the model asks for exactly that with
 * the `lookup` tool and gets a handful of rows back. Small by default, precise on
 * demand; that is the whole trick.
 */
import type { ExperienceMode } from '../../app/experience';
import type { Gender, Lang } from '../../domain/types';
import { screenForPath } from './appMap';

export interface AssistantContext {
  /** Current address. */
  path: string;
  /** Screen id from the app map, when the path is one we know. */
  screen?: string;
  /** Panels and cards open on top, outermost first — named, with ids, so "her" and "this story" can be acted on. */
  open: string[];
  /** What the screen underneath is showing: who the tree is centred on, whose stories the timeline is filtered to. */
  inView?: string[];
  /**
   * The person asking. Hebrew addresses "you" differently for a man and a woman,
   * so this decides whether every answer sounds right or grates.
   */
  meGender?: Gender;
  /** The gender the assistant's own voice sounds, so it speaks of itself consistently in Hebrew. */
  voiceGender?: 'female' | 'male';
  lang: Lang;
  mode: ExperienceMode;
  /** 'member' | 'editor' | 'admin' — what the session may do. */
  role: string;
  /** Administrator powers switched on for this device right now. */
  adminMode: boolean;
  /** This account really is an administrator, whether or not the switch is on. */
  canBeAdmin: boolean;
  /** The signed-in person's display name, when their account is joined to a circle. */
  me?: string;
  /** Whether they have a circle in the tree at all. */
  hasCircle: boolean;
  counts: { people: number; stories: number; moments: number; upcoming: number; unread: number };
  online: boolean;
  /** Edits waiting to go out. */
  pending: number;
  /** Whether it is Shabbat for them right now, with Shabbat mode on. */
  shabbatQuiet: boolean;
  /** The last error the app showed them, if any — usually the reason they pressed Help. */
  lastError?: string;
}

/** The snapshot as the model reads it: short lines, no punctuation it has to parse. */
export function describeContext(c: AssistantContext): string {
  const screen = c.screen ? `${c.screen} (${c.path})` : c.path;
  const who =
    c.meGender === 'female' ? ' — a woman: in Hebrew speak to her in the feminine'
    : c.meGender === 'male' ? ' — a man: in Hebrew speak to him in the masculine'
    : ' — gender not recorded: in Hebrew address them in the plural';
  const lines = [
    `screen: ${screen}`,
    c.open.length ? `open on top: ${c.open.join(' → ')}` : 'open on top: nothing',
    ...(c.inView?.length ? [`on screen: ${c.inView.join('; ')}`] : []),
    `mode: ${c.mode}${c.mode === 'simple' ? ' (the globe, timeline, calendar and bell are hidden)' : ''}`,
    `language: ${c.lang === 'he' ? 'Hebrew' : 'English'}`,
    `they are: ${c.me ?? 'signed in but not joined to a circle yet'}${who}`,
    `your voice: ${c.voiceGender === 'male' ? "a man's — in Hebrew speak of yourself in the masculine" : "a woman's — in Hebrew speak of yourself in the feminine"}`,
    `role: ${c.role}${c.canBeAdmin ? (c.adminMode ? ' · admin mode ON' : ' · admin mode off') : ''}`,
    `family: ${c.counts.people} people, ${c.counts.stories} stories, ${c.counts.moments} pictures and days, ${c.counts.upcoming} occasions coming up`,
  ];
  if (!c.online) lines.push('they are OFFLINE right now');
  if (c.pending > 0) lines.push(`${c.pending} change(s) waiting to be sent`);
  if (c.shabbatQuiet) lines.push('Shabbat mode is on and it is Shabbat where they are, so the bell is quiet');
  if (c.counts.unread > 0) lines.push(`${c.counts.unread} unread notification(s)`);
  if (c.lastError) lines.push(`the last thing that went wrong on screen: ${c.lastError}`);
  return lines.join('\n');
}

/** Best-effort screen id for a path, used when building the snapshot. */
export function screenIdFor(path: string): string | undefined {
  return screenForPath(path)?.id;
}
